import { clientV3SDK } from "@istari-frontend/api-client";
import { V3Models } from "@istari/istari-client";

import type { Conversation, ConversationMessage } from "~/ai-chat/model/conversation";

export interface ListConversationsParams {
  // Case-insensitive substring over title + message content.
  q?: string | null;
  // Default false hides archived conversations.
  includeArchived?: boolean;
  // Keyset cursor: pass the prior page's `nextPage`.
  cursor?: string | null;
  size?: number;
}

export interface ConversationPage {
  items: Conversation[];
  nextPage: string | null;
}

export async function listConversations(params: ListConversationsParams = {}): Promise<ConversationPage> {
  const page = await clientV3SDK.listAiConversations({
    q: params.q ?? undefined,
    includeArchived: params.includeArchived ?? false,
    cursor: params.cursor ?? undefined,
    size: params.size,
  });
  return { items: page.items.map(toConversation), nextPage: page.nextPage ?? null };
}

export async function setConversationArchived(conversationId: string, archived: boolean): Promise<Conversation> {
  const dto = await clientV3SDK.updateAiConversation({
    conversationId,
    updateConversationRequest: { archived },
  });
  return toConversation(dto);
}

export async function createConversation(title: string | null): Promise<Conversation> {
  const dto = await clientV3SDK.createAiConversation({ createConversationRequest: { title } });
  return toConversation(dto);
}

export async function renameConversation(conversationId: string, title: string | null): Promise<Conversation> {
  const dto = await clientV3SDK.updateAiConversation({
    conversationId,
    updateConversationRequest: { title },
  });
  return toConversation(dto);
}

export async function listConversationMessages(conversationId: string): Promise<ConversationMessage[]> {
  const dtos = await clientV3SDK.listAiConversationMessages({ conversationId });
  return dtos.map(toConversationMessage);
}

export async function appendConversationMessage(
  conversationId: string,
  role: ConversationMessage["role"],
  content: string,
): Promise<ConversationMessage> {
  const dto = await clientV3SDK.appendAiConversationMessage({
    conversationId,
    appendMessageRequest: { role: toMessageRole(role), content },
  });
  return toConversationMessage(dto);
}

function toConversation(dto: V3Models.AiConversationDto): Conversation {
  return {
    id: dto.id,
    title: dto.title ?? null,
    created: dto.created,
    updated: dto.updated,
    status: dto.status ?? null,
    archivedAt: dto.archivedAt ?? null,
  };
}

function toConversationMessage(dto: V3Models.AiMessageDto): ConversationMessage {
  return {
    id: dto.id,
    role: toDomainRole(dto.role),
    content: dto.content,
    created: dto.created,
  };
}

function toMessageRole(role: ConversationMessage["role"]): V3Models.MessageRole {
  switch (role) {
    case "user":
      return V3Models.MessageRole.User;
    case "assistant":
      return V3Models.MessageRole.Assistant;
    case "system":
      return V3Models.MessageRole.System;
    default: {
      const exhaustive: never = role;
      throw new Error(`Unknown message role: ${String(exhaustive)}`);
    }
  }
}

function toDomainRole(role: V3Models.MessageRole): ConversationMessage["role"] {
  switch (role) {
    case V3Models.MessageRole.User:
      return "user";
    case V3Models.MessageRole.Assistant:
      return "assistant";
    case V3Models.MessageRole.System:
      return "system";
    default: {
      const exhaustive: never = role;
      throw new Error(`Unknown message role: ${String(exhaustive)}`);
    }
  }
}
